import { useState, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";

interface KanbanBoardProps<T> {
  statuses: readonly string[];
  statusStyles: Record<string, string>;
  items: T[];
  getStatus: (item: T) => string;
  getId: (item: T) => string;
  /** Null for a board that only shows status: its cards cannot be dragged. */
  onStatusChange: ((id: string, status: string) => void) | null;
  renderCard: (item: T) => ReactNode;
}

export function KanbanBoard<T>({
  statuses,
  statusStyles,
  items,
  getStatus,
  getId,
  onStatusChange,
  renderCard,
}: KanbanBoardProps<T>) {
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {statuses.map((status) => {
        const columnItems = items.filter((item) => getStatus(item) === status);
        const isOver = dragOverStatus === status;

        return (
          <div
            key={status}
            className={`flex w-64 shrink-0 flex-col rounded-lg border bg-gray-50 transition-colors ${
              isOver ? "border-blue-300 bg-blue-50/50 ring-1 ring-blue-200" : "border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
              <div className="flex items-center gap-2">
                <Badge
                  className={statusStyles[status] ?? "border-transparent bg-gray-100 text-gray-600"}
                >
                  {status}
                </Badge>
                <span className="text-xs text-gray-400">{columnItems.length}</span>
              </div>
            </div>
            <div
              className="flex min-h-[100px] flex-col gap-2 p-2"
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                setDragOverStatus(status);
              }}
              onDragLeave={(e) => {
                // Only clear if leaving the column entirely (not entering a child)
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setDragOverStatus(null);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverStatus(null);
                const id = e.dataTransfer.getData("text/plain");
                if (id && onStatusChange) onStatusChange(id, status);
              }}
            >
              {columnItems.map((item) => (
                <div
                  key={getId(item)}
                  draggable={onStatusChange !== null}
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", getId(item));
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  className={`rounded-md border border-gray-200 bg-white p-3 shadow-sm transition-shadow hover:shadow ${
                    onStatusChange ? "cursor-grab active:cursor-grabbing" : ""
                  }`}
                >
                  {renderCard(item)}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
